import axios from 'axios'
import { v4 } from 'uuid'

import { AxiosHttpClient } from '@webapp/core/libs/AxiosHttpClient'
import {
  type FeatureFlagKey,
  FeatureFlagService,
} from '@webapp/core/services/FeatureFlag'
import {
  APISessionExpiredError,
  InvalidSessionMessage,
  isSessionExpiredResponse,
  redirectToSignIn,
} from '@webapp/core/services/SessionExpiry'
import { HttpError } from '@webapp/infrastructure/interfaces/services/IHttpClient'

const axiosClient = axios.create({
  headers: {
    'X-Procurify-Client': 'web:react',
    'X-Correlation-Id': v4(),
  },
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
})

export const ApiClient = new AxiosHttpClient(axiosClient)

const SESSION_EXPIRY_REDIRECT_FLAG: FeatureFlagKey =
  'PLAT_6351_REDIRECT_ON_SESSION_EXPIRED'

/**
 * Read per error rather than at module load so a LaunchDarkly change reaches
 * open tabs without a reload. Off until the service is ready.
 */
const isSessionExpiryRedirectEnabled = (): boolean => {
  try {
    return !!FeatureFlagService.getInstance().getFlag(
      SESSION_EXPIRY_REDIRECT_FLAG
    )
  } catch {
    return false
  }
}

/**
 * Registered after `AxiosHttpClient` so it sees the normalized `HttpError`.
 * Shares its 401 predicates with `redirectOnSessionExpired` on the legacy
 * v1/v3 clients (PLAT-6351). `APISessionExpiredError` is still an
 * `HttpError`, and is on Sentry's ignore list.
 */
const rejectOnSessionExpired = (error: unknown) => {
  if (
    error instanceof HttpError &&
    isSessionExpiredResponse(error.status, error.response) &&
    isSessionExpiryRedirectEnabled()
  ) {
    redirectToSignIn()
    throw new APISessionExpiredError(
      InvalidSessionMessage.MISSING,
      error.response,
      error.headers
    )
  }

  throw error
}

axiosClient.interceptors.response.use(undefined, rejectOnSessionExpired)
